<div class="review-attest">
  <agmo-loading *ngIf="isLoading"></agmo-loading>

  <ng-container *ngIf="!isLoading && statuses">
    <!-- Intro header: eyebrow → statement title → assurance (Variant B). -->
    <header class="review-attest__intro">
      <span class="review-attest__eyebrow">{{ contextCopy.eyebrow }}</span>
      <h1 class="review-attest__title">{{ contextCopy.title }}</h1>
      <p class="review-attest__assurance">{{ contextCopy.assurance }}</p>
    </header>

    <!-- Attention callout, full width above the grid. Warning when something is
         missing; otherwise info when a name/ID change is awaiting verification. -->
    <div
      *ngIf="missingSections.length"
      class="review-attest__callout review-attest__callout--warning"
      role="alert"
    >
      <strong>{{ calloutHeading }}</strong> —
      <ng-container *ngFor="let item of missingSections; let last = last"
        ><a class="review-attest__callout-link" [attr.href]="'#section-' + item.key">{{
          item.label
        }}</a><span *ngIf="!last"> · </span></ng-container
      >. Everything else looks good.
    </div>
    <div
      *ngIf="!missingSections.length && statuses.identity === 'reverify'"
      class="review-attest__callout review-attest__callout--info"
    >
      Your name/ID change is <strong>waiting for verification</strong> — confirm below to start the
      identity check.
    </div>

    <div class="review-attest__grid">
      <!-- Main column: the five sections as hairline-ruled blocks. -->
      <main class="review-attest__main">
        <section
          id="section-identity"
          class="review-attest__section"
          [class.review-attest__section--highlight]="highlightedSection === 'identity'"
        >
          <div class="review-attest__section-head">
            <h2 class="review-attest__section-title">Identity</h2>
            <agmo-status-pill [status]="statuses.identity"></agmo-status-pill>
            <agmo-button
              class="review-attest__edit"
              buttonClass="btn-link"
              [buttonLabel]="isSectionEmpty('identity') ? 'Add now' : 'Edit'"
              [disabled]="savingSection === 'identity' || discarding"
              [loading]="savingSection === 'identity'"
              (click)="openSectionSheet('identity')"
            ></agmo-button>
          </div>

          <dl class="review-attest__rows" *ngIf="!isSectionEmpty('identity'); else identityEmpty">
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Full name</dt>
              <dd class="review-attest__row-value">{{ profile.fullName }}</dd>
            </div>
            <div class="review-attest__row">
              <dt class="review-attest__row-label">ID number</dt>
              <dd class="review-attest__row-value">{{ profile.identityNo }}</dd>
            </div>
            <!-- INC-283: nationality is a read-only identity attribute (moved here
                 from Income & transaction per UAT). Never editable on this screen. -->
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Nationality</dt>
              <dd class="review-attest__row-value">{{ profile.nationality || '—' }}</dd>
            </div>
          </dl>
          <ng-template #identityEmpty>
            <p class="review-attest__empty">{{ emptyHint('identity') }}</p>
          </ng-template>

          <!-- INC-289: sets expectation without the eKYC acronym. -->
          <p *ngIf="statuses.identity !== 'reverify'" class="review-attest__note">
            Changing your name or ID needs a short identity re-check before it goes live. Everything
            else updates straight away.
          </p>

          <!-- INC-322: once a name/ID change is staged, tell the investor it's held
               pending re-verification and that they can undo it to edit again. -->
          <p *ngIf="statuses.identity === 'reverify'" class="review-attest__note">
            Your new name/ID is saved but not live yet — we'll verify it when you confirm below. Your
            current verified details stay active until then.
          </p>

          <p *ngIf="sectionErrors.identity" class="review-attest__section-error" role="alert">
            {{ sectionErrors.identity }}
          </p>

          <!-- INC-322: undo the staged name/ID change and re-enable editing.
               Shown only while a change is pending re-verification. -->
          <agmo-button
            *ngIf="statuses.identity === 'reverify' || identityReKycPending"
            class="review-attest__undo"
            buttonClass="btn-link"
            [buttonLabel]="'Undo this change'"
            [disabled]="discarding || savingSection === 'identity'"
            [loading]="discarding"
            (click)="discardPendingIdentityChange()"
          ></agmo-button>
        </section>

        <section
          id="section-contact"
          class="review-attest__section"
          [class.review-attest__section--highlight]="highlightedSection === 'contact'"
        >
          <div class="review-attest__section-head">
            <h2 class="review-attest__section-title">Contact</h2>
            <agmo-status-pill [status]="statuses.contact"></agmo-status-pill>
            <agmo-button
              class="review-attest__edit"
              buttonClass="btn-link"
              [buttonLabel]="isSectionEmpty('contact') ? 'Add now' : 'Edit'"
              [disabled]="savingSection === 'contact'"
              [loading]="savingSection === 'contact'"
              (click)="openSectionSheet('contact')"
            ></agmo-button>
          </div>

          <dl class="review-attest__rows" *ngIf="!isSectionEmpty('contact'); else contactEmpty">
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Phone</dt>
              <dd class="review-attest__row-value">{{ profile.phoneNumber }}</dd>
            </div>
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Address</dt>
              <dd class="review-attest__row-value">{{ profile.address }}</dd>
            </div>
          </dl>
          <ng-template #contactEmpty>
            <p class="review-attest__empty">{{ emptyHint('contact') }}</p>
          </ng-template>

          <p *ngIf="sectionErrors.contact" class="review-attest__section-error" role="alert">
            {{ sectionErrors.contact }}
          </p>
        </section>

        <section
          id="section-employmentIncome"
          class="review-attest__section"
          [class.review-attest__section--highlight]="highlightedSection === 'employmentIncome'"
        >
          <div class="review-attest__section-head">
            <h2 class="review-attest__section-title">Employment &amp; income</h2>
            <agmo-status-pill [status]="statuses.employmentIncome"></agmo-status-pill>
            <agmo-button
              class="review-attest__edit"
              buttonClass="btn-link"
              [buttonLabel]="isSectionEmpty('employmentIncome') ? 'Add now' : 'Edit'"
              [disabled]="savingSection === 'employmentIncome'"
              [loading]="savingSection === 'employmentIncome'"
              (click)="openSectionSheet('employmentIncome')"
            ></agmo-button>
          </div>

          <dl class="review-attest__rows" *ngIf="!isSectionEmpty('employmentIncome'); else employmentIncomeEmpty">
            <div class="review-attest__row" *ngIf="occupationTypeName">
              <dt class="review-attest__row-label">Employment status</dt>
              <dd class="review-attest__row-value">{{ occupationTypeName }}</dd>
            </div>
            <div class="review-attest__row" *ngIf="employmentSectorName">
              <dt class="review-attest__row-label">Sector</dt>
              <dd class="review-attest__row-value">{{ employmentSectorName }}</dd>
            </div>
            <div class="review-attest__row" *ngIf="grossAnnualIncomeName">
              <dt class="review-attest__row-label">Annual income</dt>
              <dd class="review-attest__row-value">{{ grossAnnualIncomeName }}</dd>
            </div>
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Purpose of transaction</dt>
              <dd class="review-attest__row-value">{{ transactionPurposeName }}</dd>
            </div>
          </dl>
          <ng-template #employmentIncomeEmpty>
            <p class="review-attest__empty">{{ emptyHint('employmentIncome') }}</p>
          </ng-template>

          <p *ngIf="sectionErrors.employmentIncome" class="review-attest__section-error" role="alert">
            {{ sectionErrors.employmentIncome }}
          </p>
        </section>

        <section
          id="section-bank"
          class="review-attest__section"
          [class.review-attest__section--highlight]="highlightedSection === 'bank'"
        >
          <div class="review-attest__section-head">
            <h2 class="review-attest__section-title">Bank</h2>
            <agmo-status-pill [status]="statuses.bank"></agmo-status-pill>
            <agmo-button
              class="review-attest__edit"
              buttonClass="btn-link"
              [buttonLabel]="isSectionEmpty('bank') ? 'Add now' : 'Edit'"
              [disabled]="savingSection === 'bank'"
              [loading]="savingSection === 'bank'"
              (click)="openSectionSheet('bank')"
            ></agmo-button>
          </div>

          <dl class="review-attest__rows" *ngIf="!isSectionEmpty('bank'); else bankEmpty">
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Bank</dt>
              <dd class="review-attest__row-value">{{ paymentMethod.bankName }}</dd>
            </div>
            <div class="review-attest__row">
              <dt class="review-attest__row-label">Account holder</dt>
              <dd class="review-attest__row-value">{{ paymentMethod.accountHolder }}</dd>
            </div>
            <div class="review-attest__row" *ngIf="maskedAccountNumber">
              <dt class="review-attest__row-label">Account number</dt>
              <dd class="review-attest__row-value">{{ maskedAccountNumber }}</dd>
            </div>
          </dl>
          <ng-template #bankEmpty>
            <p class="review-attest__empty">{{ emptyHint('bank') }}</p>
          </ng-template>

          <p *ngIf="sectionErrors.bank" class="review-attest__section-error" role="alert">
            {{ sectionErrors.bank }}
          </p>
        </section>
      </main>

      <!-- Side rail: the attest box (where the checkbox + final CTA live), then
           the "Why we ask" panel. -->
      <aside class="review-attest__rail">
        <div class="review-attest__attest-box">
          <!-- Blockers: only when a required section is still missing. -->
          <ul
            *ngIf="!canConfirmEverythingCorrect && missingSectionMessages.length"
            class="review-attest__blockers"
            role="alert"
          >
            <li *ngFor="let message of missingSectionMessages">{{ message }}</li>
          </ul>

          <label class="review-attest__attest-label">
            <input
              type="checkbox"
              class="review-attest__attest-checkbox"
              [(ngModel)]="attestationChecked"
            />
            <span>I confirm my details are true, accurate and complete.</span>
          </label>

          <agmo-button
            class="review-attest__cta"
            buttonClass="btn-primary"
            [buttonLabel]="confirmCtaLabel"
            [disabled]="!canSubmitAttestation"
            [loading]="isConfirming"
            (click)="confirmEverythingCorrect()"
          ></agmo-button>

          <p class="review-attest__privacy">
            We ask because Malaysian securities regulations require us to keep investor records
            current. Your details are used to verify your identity on the platform.
          </p>
        </div>

        <div class="review-attest__why">
          <h3 class="review-attest__why-title">Why we ask</h3>
          <p class="review-attest__why-text">
            Regulations require us to keep investor records current. Confirming takes a minute and is
            logged as your attestation.
          </p>
          <p class="review-attest__why-text">
            Changing your name or ID triggers a short re-verification. Everything else updates
            instantly.
          </p>
        </div>
      </aside>
    </div>
  </ng-container>
</div>
